import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  Save, Plus, Trash2, Users, AlertTriangle, Sparkles,
  ChevronDown, ChevronUp, Loader2, CheckCircle2, XCircle, Edit3, Info, Flag, Shield
} from 'lucide-react';
import axios from 'axios';

import { API_URL } from '../../utils/api';
import { getActiveProfile } from '../../config/arizona-sped';

const profile = getActiveProfile();
const DISABILITY_CATEGORIES = profile.disabilityCategories;

const AGREEMENT_OPTIONS = ['Agree', 'Disagree', 'Pending'];

const CONFIDENCE_STYLE = {
  high: 'bg-green-50 text-green-700 border-green-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-gray-50 text-gray-500 border-gray-200',
};

function mergeContent(existing) {
  const defaults = {
    recommendations: '', disability_category: '', disability_subcategories: [],
    secondary_category: '', secondary_subcategories: [], clarification: '',
    team: { participants: [] }, _sources: {},
  };
  if (!existing || typeof existing !== 'object') return defaults;
  if (existing.notes && !existing.recommendations && !existing.disability_category) {
    return { ...defaults, recommendations: existing.notes };
  }
  // Backward compat: migrate single subcategory string → array
  const merged = { ...defaults, ...existing, team: { ...defaults.team, ...(existing.team || {}) } };
  if (existing.disability_subcategory && !existing.disability_subcategories?.length) {
    merged.disability_subcategories = [existing.disability_subcategory];
  }
  if (existing.secondary_subcategory && !existing.secondary_subcategories?.length) {
    merged.secondary_subcategories = [existing.secondary_subcategory];
  }
  if (!Array.isArray(merged.disability_subcategories)) merged.disability_subcategories = [];
  if (!Array.isArray(merged.secondary_subcategories)) merged.secondary_subcategories = [];
  return merged;
}

/** Finds the category config object by label (backward compat) or id */
function findCategory(value) {
  if (!value) return null;
  return DISABILITY_CATEGORIES.find(c => c.label === value || c.id === value) || null;
}

export default function EligibilityDeterminationEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  const [aiOpen, setAiOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [advisoryData, setAdvisoryData] = useState(null);
  const [aiMeta, setAiMeta] = useState(null);
  const [draftStates, setDraftStates] = useState({});

  useEffect(() => { setContent(mergeContent(section?.content)); }, [section?.section_id]);

  const update = (key, value) => setContent(prev => ({ ...prev, [key]: value }));

  const handleCategoryChange = (field, value) => {
    const subField = field === 'disability_category' ? 'disability_subcategories' : 'secondary_subcategories';
    setContent(prev => ({ ...prev, [field]: value, [subField]: [] }));
  };

  const toggleSubcategory = (field, label) => {
    setContent(prev => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(label) ? arr.filter(x => x !== label) : [...arr, label] };
    });
  };

  const addParticipant = () => {
    setContent(prev => ({
      ...prev, team: {
        ...prev.team,
        participants: [...(prev.team?.participants || []), { id: `p_${Date.now()}`, name: '', role: '', agreement: 'Pending', date: '' }],
      },
    }));
  };
  const removeParticipant = (idx) => {
    setContent(prev => ({ ...prev, team: { ...prev.team, participants: prev.team.participants.filter((_, i) => i !== idx) } }));
  };
  const updateParticipant = (idx, field, value) => {
    setContent(prev => ({
      ...prev, team: { ...prev.team, participants: prev.team.participants.map((p, i) => i === idx ? { ...p, [field]: value } : p) },
    }));
  };

  const handleSave = () => {
    const { _sources, ...saveData } = content;
    if (_sources && Object.keys(_sources).length > 0) saveData._sources = _sources;
    onSave(saveData);
  };

  // --- AI Advisory ---
  const generateAdvisory = async () => {
    setGenerating(true);
    setAdvisoryData(null);
    setAiMeta(null);
    setDraftStates({});
    try {
      const res = await axios.post(
        `${API_URL}/api/iep-met/cases/${caseData.case_id}/met-sections/met_eligibility/draft`,
        {}, { withCredentials: true }
      );
      const output = res.data?.output;
      if (output) {
        setAdvisoryData(output);
        setAiMeta({
          dataSources: output.data_sources_used || [],
          notes: output.notes || '',
          gaps: output.gaps_identified || [],
          model: res.data?.model_used,
          inputSnapshot: res.data?.input_snapshot,
        });
        const init = {};
        if (output.draft_recommendation) init.recommendation = 'pending';
        if (output.draft_clarification) init.clarification = 'pending';
        setDraftStates(init);
      }
    } catch (err) {
      console.error('AI advisory failed', err);
      setAiMeta({ error: err.response?.data?.detail || 'AI analysis failed. Please try again.' });
    } finally {
      setGenerating(false);
    }
  };

  const acceptDraft = (field) => {
    if (field === 'recommendation') {
      setContent(prev => ({
        ...prev, recommendations: advisoryData.draft_recommendation,
        _sources: { ...(prev._sources || {}), recommendations: 'ai_suggested' },
      }));
    } else if (field === 'clarification') {
      setContent(prev => ({
        ...prev, clarification: advisoryData.draft_clarification,
        _sources: { ...(prev._sources || {}), clarification: 'ai_suggested' },
      }));
    }
    setDraftStates(prev => ({ ...prev, [field]: 'accepted' }));
  };

  const editDraft = (field) => {
    if (field === 'recommendation') {
      setContent(prev => ({
        ...prev, recommendations: advisoryData.draft_recommendation,
        _sources: { ...(prev._sources || {}), recommendations: 'ai_edited' },
      }));
    } else if (field === 'clarification') {
      setContent(prev => ({
        ...prev, clarification: advisoryData.draft_clarification,
        _sources: { ...(prev._sources || {}), clarification: 'ai_edited' },
      }));
    }
    setDraftStates(prev => ({ ...prev, [field]: 'edited' }));
  };

  const rejectDraft = (field) => {
    setDraftStates(prev => ({ ...prev, [field]: 'rejected' }));
  };

  const pendingCount = Object.values(draftStates).filter(s => s === 'pending').length;
  const source = content._sources || {};

  const primaryCat = findCategory(content.disability_category);
  const secondaryCat = findCategory(content.secondary_category);

  return (
    <div className="max-w-4xl space-y-6" data-testid="eligibility-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="eligibility-title">Eligibility Determination</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <Badge className="mt-2 bg-[#F3F4F1] text-[#595959] text-[10px]">{profile.label} Profile</Badge>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="eligibility-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Advisory notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="eligibility-advisory">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">Eligibility determination is made by the evaluation team. AI provides advisory support only and cannot make legal eligibility decisions.</p>
      </div>

      {/* AI Advisory Panel */}
      <div className="bg-gradient-to-r from-[#5A8A5A]/5 to-[#3B6A9B]/5 border border-[#5A8A5A]/20 rounded-2xl overflow-hidden" data-testid="ai-eligibility-panel">
        <button className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/40 transition-colors" onClick={() => setAiOpen(!aiOpen)} data-testid="eligibility-ai-toggle">
          <div className="flex items-center gap-2.5">
            <Sparkles className="h-4 w-4 text-[#5A8A5A]" />
            <span className="text-sm font-medium text-[#1C1C1C]">AI Eligibility Advisory</span>
            <Badge className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200">Advisory Only</Badge>
          </div>
          {aiOpen ? <ChevronUp className="h-4 w-4 text-[#595959]" /> : <ChevronDown className="h-4 w-4 text-[#595959]" />}
        </button>

        {aiOpen && (
          <div className="px-5 pb-5 border-t border-[#5A8A5A]/10">
            <div className="flex items-center justify-between mt-3 mb-4">
              <p className="text-xs text-[#595959] max-w-lg">
                AI analyzes evaluation data to provide category considerations, evidence basis, caution areas, and draft language. The team makes all final decisions.
              </p>
              <Button onClick={generateAdvisory} disabled={generating}
                className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl text-xs h-8 shrink-0" data-testid="generate-eligibility-advisory-btn">
                {generating ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Analyzing...</>
                  : <><Sparkles className="h-3 w-3 mr-1.5" />{advisoryData ? 'Re-analyze' : 'Analyze Eligibility Data'}</>}
              </Button>
            </div>

            {aiMeta?.error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="eligibility-ai-error">
                <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{aiMeta.error}</p>
              </div>
            )}

            {advisoryData && (
              <div className="space-y-4" data-testid="eligibility-advisory-results">
                {aiMeta && !aiMeta.error && (
                  <div className="bg-white/60 border border-[#E5E5E5] rounded-xl p-3" data-testid="eligibility-ai-meta">
                    <div className="flex items-start gap-4 text-xs text-[#595959]">
                      <div className="flex-1">
                        {aiMeta.notes && <p className="mb-1"><strong>AI Notes:</strong> {aiMeta.notes}</p>}
                        {aiMeta.dataSources?.length > 0 && <p className="mb-1"><strong>Data used:</strong> {aiMeta.dataSources.join(', ')}</p>}
                        {aiMeta.gaps?.length > 0 && <p className="text-amber-600"><strong>Data gaps:</strong> {aiMeta.gaps.join('; ')}</p>}
                      </div>
                      {pendingCount > 0 && (
                        <Button variant="outline" size="sm" className="rounded-lg text-xs h-7 border-green-300 text-green-700 hover:bg-green-50 shrink-0"
                          onClick={() => { Object.entries(draftStates).forEach(([k, v]) => { if (v === 'pending') acceptDraft(k); }); }}
                          data-testid="accept-all-eligibility-btn">
                          <CheckCircle2 className="h-3 w-3 mr-1" />Accept All
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Category Analysis */}
                {advisoryData.category_analysis?.length > 0 && (
                  <div className="space-y-3" data-testid="category-analysis">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#595959]">Category Considerations</h4>
                    {advisoryData.category_analysis.map((cat, i) => (
                      <div key={i} className="border border-[#E5E5E5] rounded-xl overflow-hidden bg-white" data-testid={`category-${i}`}>
                        <div className="px-4 py-2.5 bg-[#F3F4F1] flex items-center gap-2">
                          <Shield className="h-3.5 w-3.5 text-[#5A8A5A]" />
                          <span className="text-xs font-semibold text-[#1C1C1C]">{cat.category}</span>
                          {cat.confidence && <Badge className={`text-[10px] border ${CONFIDENCE_STYLE[cat.confidence]}`}>{cat.confidence}</Badge>}
                        </div>
                        <div className="p-4 space-y-2">
                          {cat.evidence_for?.length > 0 && (
                            <div>
                              <p className="text-[10px] font-bold text-green-700 mb-0.5">Supporting Evidence</p>
                              <ul className="space-y-0.5">{cat.evidence_for.map((e, j) => <li key={j} className="text-xs text-green-800">+ {e}</li>)}</ul>
                            </div>
                          )}
                          {cat.evidence_against?.length > 0 && (
                            <div>
                              <p className="text-[10px] font-bold text-red-600 mb-0.5">Evidence Against</p>
                              <ul className="space-y-0.5">{cat.evidence_against.map((e, j) => <li key={j} className="text-xs text-red-700">- {e}</li>)}</ul>
                            </div>
                          )}
                          {cat.caution_areas?.length > 0 && (
                            <div>
                              <p className="text-[10px] font-bold text-amber-600 mb-0.5">Caution Areas</p>
                              <ul className="space-y-0.5">{cat.caution_areas.map((c, j) => <li key={j} className="text-xs text-amber-700">! {c}</li>)}</ul>
                            </div>
                          )}
                          {cat.missing_data?.length > 0 && (
                            <div>
                              <p className="text-[10px] font-bold text-gray-500 mb-0.5">Missing Data</p>
                              <ul className="space-y-0.5">{cat.missing_data.map((m, j) => <li key={j} className="text-[10px] text-gray-500">? {m}</li>)}</ul>
                            </div>
                          )}
                          {cat.recommendation && <p className="text-xs text-[#595959] italic mt-1 pt-1 border-t border-[#E5E5E5]">{cat.recommendation}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Exclusionary Factors */}
                {advisoryData.exclusionary_factors?.length > 0 && (
                  <div className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid="exclusionary-factors">
                    <h4 className="text-xs font-bold text-[#595959] mb-2 flex items-center gap-1.5">
                      <Flag className="h-3.5 w-3.5 text-red-500" />Exclusionary Factors
                    </h4>
                    {advisoryData.exclusionary_factors.map((f, i) => (
                      <div key={i} className="flex items-start gap-2 mb-1.5">
                        <Badge className={`text-[10px] shrink-0 ${
                          f.status === 'addressed' ? 'bg-green-50 text-green-700 border-green-200' :
                          f.status === 'not_addressed' ? 'bg-red-50 text-red-600 border-red-200' :
                          'bg-amber-50 text-amber-600 border-amber-200'
                        }`}>{f.status}</Badge>
                        <div>
                          <p className="text-xs text-[#1C1C1C]">{f.factor}</p>
                          {f.notes && <p className="text-[10px] text-[#595959]">{f.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Draft Recommendation */}
                {advisoryData.draft_recommendation && (
                  <DraftBlock
                    label="Draft Recommendation"
                    text={advisoryData.draft_recommendation}
                    state={draftStates.recommendation}
                    onAccept={() => acceptDraft('recommendation')}
                    onEdit={() => editDraft('recommendation')}
                    onReject={() => rejectDraft('recommendation')}
                    testId="draft-recommendation"
                  />
                )}

                {/* Draft Clarification */}
                {advisoryData.draft_clarification && (
                  <DraftBlock
                    label="Draft Clarification / Rationale"
                    text={advisoryData.draft_clarification}
                    state={draftStates.clarification}
                    onAccept={() => acceptDraft('clarification')}
                    onEdit={() => editDraft('clarification')}
                    onReject={() => rejectDraft('clarification')}
                    testId="draft-clarification"
                  />
                )}

                {aiMeta?.inputSnapshot && (
                  <div className="text-[10px] text-[#595959]/60 flex items-center gap-2">
                    <Info className="h-3 w-3" />
                    <span>Sources: {aiMeta.inputSnapshot.data_sources?.join(', ') || 'none'}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="recommendations-section">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959]">Eligibility Determination Recommendations</h3>
          {source.recommendations === 'ai_suggested' && <Badge className="text-[10px] bg-[#5A8A5A]/10 text-[#5A8A5A]">AI</Badge>}
          {source.recommendations === 'ai_edited' && <Badge className="text-[10px] bg-blue-50 text-blue-600">AI Edited</Badge>}
        </div>
        <Textarea value={content.recommendations} onChange={e => update('recommendations', e.target.value)}
          placeholder="State the team's eligibility recommendations..." rows={4}
          className="rounded-xl border-[#E5E5E5] text-sm" data-testid="recommendations-input" />
      </div>

      {/* Category Selection — Primary + Multi-Select Subcategories */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="category-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">Category of Disability</h3>

        {/* Primary Category */}
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Primary Category</label>
            <Select value={content.disability_category || undefined} onValueChange={v => handleCategoryChange('disability_category', v)}>
              <SelectTrigger className="rounded-xl text-sm h-9" data-testid="primary-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {DISABILITY_CATEGORIES.map(c => (
                  <SelectItem key={c.id} value={c.label}>
                    {c.label}
                    {c.note && <span className="text-[10px] text-[#595959] ml-1">({c.note})</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Primary Subcategories — multi-select checkboxes */}
          {primaryCat?.subcategories && (
            <SubcategoryChips
              catConfig={primaryCat}
              selected={content.disability_subcategories || []}
              onToggle={(label) => toggleSubcategory('disability_subcategories', label)}
              testIdPrefix="primary"
            />
          )}

          {/* Secondary Category */}
          <div className="pt-2 border-t border-[#E5E5E5]">
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Secondary Category (if applicable)</label>
            <Select value={content.secondary_category || 'none'} onValueChange={v => handleCategoryChange('secondary_category', v === 'none' ? '' : v)}>
              <SelectTrigger className="rounded-xl text-sm h-9" data-testid="secondary-category"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {DISABILITY_CATEGORIES.map(c => (
                  <SelectItem key={c.id} value={c.label}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Secondary Subcategories — multi-select checkboxes */}
          {secondaryCat?.subcategories && (
            <SubcategoryChips
              catConfig={secondaryCat}
              selected={content.secondary_subcategories || []}
              onToggle={(label) => toggleSubcategory('secondary_subcategories', label)}
              testIdPrefix="secondary"
            />
          )}
        </div>
      </div>

      {/* Clarification */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="clarification-section">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959]">Clarification / Explanation</h3>
          {source.clarification === 'ai_suggested' && <Badge className="text-[10px] bg-[#5A8A5A]/10 text-[#5A8A5A]">AI</Badge>}
          {source.clarification === 'ai_edited' && <Badge className="text-[10px] bg-blue-50 text-blue-600">AI Edited</Badge>}
        </div>
        <p className="text-xs text-[#595959] mb-2">Provide any clarification, rationale, or explanation for the eligibility decision, including how data supports the selected category.</p>
        <Textarea value={content.clarification} onChange={e => update('clarification', e.target.value)}
          placeholder="Provide any clarification or rationale for the eligibility decision..." rows={4}
          className="rounded-xl border-[#E5E5E5] text-sm" data-testid="clarification-input" />
      </div>

      {/* Team Signatures */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid="team-section">
        <div className="flex items-center justify-between px-5 py-3.5 bg-[#F3F4F1]">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-[#5A8A5A]" />
            <h3 className="font-medium text-sm text-[#1C1C1C]">Evaluation Eligibility Team ({content.team?.participants?.length || 0})</h3>
          </div>
          <Button variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={addParticipant} data-testid="add-team-member">
            <Plus className="h-3 w-3 mr-1" />Add Member
          </Button>
        </div>
        <div className="p-5">
          {(content.team?.participants || []).length === 0 ? (
            <p className="text-sm text-[#595959] text-center py-4">No team members added yet.</p>
          ) : (
            <div className="space-y-3">
              {content.team.participants.map((p, idx) => (
                <div key={p.id || idx} className="border border-[#E5E5E5] rounded-xl p-4 relative group" data-testid={`team-member-${idx}`}>
                  <button onClick={() => removeParticipant(idx)} className="absolute top-3 right-3 p-1 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition">
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </button>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Name</label>
                      <Input value={p.name} onChange={e => updateParticipant(idx, 'name', e.target.value)} placeholder="Full name" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Role</label>
                      <Input value={p.role} onChange={e => updateParticipant(idx, 'role', e.target.value)} placeholder="Role / title" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Agreement</label>
                      <Select value={p.agreement || undefined} onValueChange={v => updateParticipant(idx, 'agreement', v)}>
                        <SelectTrigger className="rounded-xl text-sm h-9"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>{AGREEMENT_OPTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Date</label>
                      <Input type="date" value={p.date} onChange={e => updateParticipant(idx, 'date', e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="eligibility-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Eligibility Determination'}
        </Button>
      </div>
    </div>
  );
}

function SubcategoryChips({ catConfig, selected, onToggle, testIdPrefix }) {
  const isMulti = catConfig.selectionMode === 'multi';
  const rule = catConfig.rules;
  const count = selected.length;
  const belowMin = rule?.minRequired && count < rule.minRequired;

  return (
    <div className="bg-[#F3F4F1]/50 border border-[#E5E5E5] rounded-xl p-4" data-testid={`${testIdPrefix}-subcategories`}>
      <div className="flex items-center gap-2 mb-2.5">
        <label className="text-xs font-medium text-[#1C1C1C]">
          {isMulti ? 'Select Areas' : 'Subcategory'}
          <span className="text-[10px] text-[#595959] font-normal ml-1">({catConfig.label})</span>
        </label>
        {isMulti && <Badge className="text-[10px] bg-[#3B6A9B]/10 text-[#3B6A9B]">Multi-select</Badge>}
        {count > 0 && <Badge className="text-[10px] bg-[#5A8A5A]/10 text-[#5A8A5A]">{count} selected</Badge>}
      </div>
      {rule?.advisory && (
        <p className={`text-[10px] mb-2.5 ${belowMin ? 'text-amber-600 font-medium' : 'text-[#595959]'}`}>
          {belowMin && <AlertTriangle className="h-3 w-3 inline mr-1 -mt-0.5" />}
          {rule.advisory}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {catConfig.subcategories.map(sub => {
          const active = selected.includes(sub.label);
          return (
            <button
              key={sub.id}
              type="button"
              onClick={() => onToggle(sub.label)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                active
                  ? 'bg-[#5A8A5A] text-white border-[#5A8A5A]'
                  : 'bg-white text-[#595959] border-[#E5E5E5] hover:border-[#5A8A5A]/40 hover:bg-[#5A8A5A]/5'
              }`}
              data-testid={`${testIdPrefix}-sub-${sub.id}`}
            >
              {active && <CheckCircle2 className="h-3 w-3 inline mr-1 -mt-0.5" />}
              {sub.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DraftBlock({ label, text, state, onAccept, onEdit, onReject, testId }) {
  const isPending = state === 'pending';
  return (
    <div className={`border rounded-xl overflow-hidden ${
      state === 'accepted' ? 'bg-green-50/30 border-green-200' :
      state === 'edited' ? 'bg-blue-50/30 border-blue-200' :
      state === 'rejected' ? 'bg-gray-50 border-gray-200 opacity-50' :
      'border-[#E5E5E5]'
    }`} data-testid={testId}>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-semibold text-[#5A8A5A] uppercase tracking-wider">{label}</span>
          {state && state !== 'pending' && (
            <Badge className={`text-[10px] ${
              state === 'accepted' ? 'bg-green-100 text-green-700' :
              state === 'edited' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
            }`}>{state === 'accepted' ? 'Accepted' : state === 'edited' ? 'Accepted (to edit)' : 'Rejected'}</Badge>
          )}
        </div>
        <p className="text-sm text-[#1C1C1C] whitespace-pre-wrap leading-relaxed">{text}</p>
        {isPending && (
          <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-[#E5E5E5]">
            <Button variant="outline" size="sm" onClick={onAccept} className="h-7 rounded-lg text-xs text-green-700 border-green-300 hover:bg-green-50" data-testid={`${testId}-accept`}>
              <CheckCircle2 className="h-3 w-3 mr-1" />Accept
            </Button>
            <Button variant="outline" size="sm" onClick={onEdit} className="h-7 rounded-lg text-xs text-blue-600 border-blue-300 hover:bg-blue-50" data-testid={`${testId}-edit`}>
              <Edit3 className="h-3 w-3 mr-1" />Accept & Edit
            </Button>
            <Button variant="outline" size="sm" onClick={onReject} className="h-7 rounded-lg text-xs text-red-400 border-red-200 hover:bg-red-50" data-testid={`${testId}-reject`}>
              <XCircle className="h-3 w-3 mr-1" />Reject
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
